import { useSelector } from "react-redux";
import { AppDispatch } from "store";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import { FormatDateIso, FormatDateShort } from "utils/dateUtils";
import { IOutputItem } from "Dashboard/scenes/Stock/domain/IOutputItem";
import ReturnSingleItemModal from "./ReturnSingleItemModal";
import classesStock from "../../../popover.module.scss";

import {
  selectState,
  returnOutput,
  setAllItemsModal,
  setReturnPopover,
} from "../outputSlice";
import { ICreateReturnOutput } from "Dashboard/scenes/Stock/domain/ICreateReturnOutput";
import ReturnAllItemsModal from "./ReturnAllItemsModal";
import { toast } from "react-toastify";
import InfoTooltip from "components/Popup/InfoTooltip";
import NumberInput from "Dashboard/components/NumberInput";
import { FormatNumberOrNull } from "utils/numberUtils";
import { ChangeEvent } from "react";

interface IProps {
  inputValues: { [inputId: number]: string };
  setInputValues: React.Dispatch<
    React.SetStateAction<{
      [inputId: number]: string;
    }>
  >;
  note: string;
  setNote: React.Dispatch<React.SetStateAction<string>>;
  skipCreatingReturnOutput: boolean;
  setSkipCreatingReturnOutput: React.Dispatch<React.SetStateAction<boolean>>;
  dispatch: AppDispatch;
  length: number;
  setClickOutsideActive: React.Dispatch<React.SetStateAction<boolean>>;
}

const ReturnOutput = ({
  inputValues,
  setInputValues,
  note,
  setNote,
  skipCreatingReturnOutput,
  setSkipCreatingReturnOutput,
  dispatch,
  length,
  setClickOutsideActive,
}: IProps) => {

  const { outputForEdit } = useSelector(selectState);

  if (outputForEdit === null) {
    return (<>GREŠKA</>);
  }

  const handleInputChange = (inputId: number, value: string) => {
    setInputValues((prevValues) => ({
      ...prevValues,
      [inputId]: value,
    }));
  };

  const handleClickSkipCreatingReturnOutputCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setSkipCreatingReturnOutput(checked);
  };

  const openAllItemsModal = () => {
    let data: ICreateReturnOutput = getSubmitData();
    if (data.items.length === 0) {
      toast.info("Potrebno je da unesete količinu za povraćaj");
      return;
    }

    dispatch(setAllItemsModal(true));
    setClickOutsideActive(false);
  }

  const onSubmitSingle = async (
    item: IOutputItem,
    quantityAmount: number
  ) => {
    let data: ICreateReturnOutput = {
      items: [{ outputItemId: item.outputItemId, quantityAmount }],
      date: FormatDateIso(new Date()),
      note: note,
      skipCreatingReturnOutput: false,
    };
    try {
      await dispatch(
        returnOutput({
          outputId: outputForEdit.outputId,
          params: data,
        })
      ).unwrap();
      handleSuccessfulReturn();
    } catch (status) {
      toast.error("Povraćaj nije zabeležen zbog neočekivane greške");
    }
  };

  const onSubmitAll = async () => {
    let data: ICreateReturnOutput = getSubmitData();

    try {
      await dispatch(
        returnOutput({
          outputId: outputForEdit.outputId,
          params: data,
        })
      ).unwrap();
      handleSuccessfulReturn();
    } catch (status) {
      if (status === 403) {
        toast.error("Nije dozvoljen povraćaj lagera (da li ste zaposleni u firmi?)");
      } else {
        toast.error("Povraćaj nije zabeležen zbog neočekivane greške");
      }
    }
  };

  const getSubmitData = (): ICreateReturnOutput => {
    let data: ICreateReturnOutput = {
      items: [],
      date: FormatDateIso(new Date()),
      note: note,
      skipCreatingReturnOutput: skipCreatingReturnOutput,
    };

    outputForEdit.items.forEach((item) => {
      const quantityAmount = Number(inputValues[item.inputId]);
      if (!isNaN(quantityAmount) && quantityAmount > 0) {
        data.items.push({ outputItemId: item.outputItemId, quantityAmount });
      }
    });
    return data;
  }

  const handleSuccessfulReturn = () => {
    dispatch(setReturnPopover({
      isOpen: false
    }));
    setInputValues({});
    toast.success("Povraćaj je zabeležen");
  }

  return (
    <section className={classesStock.returnOutputPopover}>
      <div className={classesStock.outputTable}>
        <div className={`${classesStock.outputRow} ${classesStock.header}`}>
          <div>br otpr. rač.</div>
          <div>naziv</div>
          <div>količina</div>
          <div>datum</div>
          <div>VRAĆAM</div>
        </div>
        {outputForEdit.items &&
          outputForEdit.items.map((item, index) => {
            return (
              <div className={classesStock.outputRow} key={index}>
                <span className={`${index === 0 ? classesStock.firstCol : ""}`}>
                  {index === 0 && outputForEdit.outputId}
                </span>
                <span className={classesStock.secondCol}>{item.itemName}</span>

                {item.quantityAmount === item.remainingQuantityAmount
                  ? (
                    <span className={classesStock.thirdCol}>
                      <input disabled value={item.quantityAmount} />
                      {item.unit.name}
                    </span>
                  )
                  : (
                    <InfoTooltip
                      tooltip={`Preostalo je ${item.remainingQuantityAmount} ${item.unit.name} od inicijalno izdatih ${item.quantityAmount} ${item.unit.name}`}
                      classesProp={classesStock.thirdCol}
                      element="span">
                      <input disabled value={`${FormatNumberOrNull(item.remainingQuantityAmount, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} (${FormatNumberOrNull(item.quantityAmount, { minimumFractionDigits: 0, maximumFractionDigits: 2 })})`} />
                      {item.unit.name}
                    </InfoTooltip>

                  )}

                <span className={`${index === 0 ? classesStock.fourthCol : ""}`}>
                  {index === 0 && FormatDateShort(outputForEdit.date)}
                </span>
                <span className={classesStock.fifthCol}>
                  <NumberInput
                    formatAs="quantity"
                    value={inputValues[item.inputId] || ""}
                    onChange={(
                      value: string,
                      unmaskedValue: string,
                      parsedValue: number) => {
                      const newValue = Math.min(
                        parsedValue,
                        item.remainingQuantityAmount
                      );
                      handleInputChange(item.inputId, newValue.toString());
                    }
                    }
                  />
                </span>
              </div>
            );
          })}
      </div>
      <div className={classesStock.actions}>
        <div className={classesStock.top}>
          <input type="checkbox"
            checked={skipCreatingReturnOutput}
            onChange={handleClickSkipCreatingReturnOutputCheckbox} />
          <label>ne pravi povratnu otpremnicu</label>
        </div>
        <div className={classesStock.bot}>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div onClick={() => openAllItemsModal()} className={classesStock.iconDiv}>
            <FontAwesomeIcon icon={faLongArrowAltDown} className={classesStock.icon} />
          </div>
        </div>
      </div>
      <ReturnSingleItemModal
        onSubmitSingle={onSubmitSingle}
        setClickOutsideActive={setClickOutsideActive}
      />
      <ReturnAllItemsModal
        onSubmitAll={onSubmitAll}
        setClickOutsideActive={setClickOutsideActive}
      />
    </section>
  );
};

export default ReturnOutput;
